import React, { useEffect, useMemo, useState } from 'react'
import { withTranslation } from 'react-i18next'
import withRouter from '../../../components/Common/withRouter'
import GlobalLoader from '../../../components/Common/GlobalLoader'
import { createSelector } from "reselect"
import { useSelector, useDispatch } from "react-redux"
import { getStockTransactionProduct, getProduct, getProductCurrentStock } from "../../../slice/thunks"
import { Card, CardBody, Col, Row, Table } from 'reactstrap'
import { useParams } from 'react-router-dom'

function StockTransactionDetails(props) {
    const dispatch = useDispatch()
    const { id } = useParams()
    const [isLoading, setIsLoading] = useState(true)

    const outputLayoutState = (state) => state.Settings
    const OutputsProperties = createSelector(
        outputLayoutState,
        (setting) => ({
            stockTransactionProduct: setting.stockTransactionProduct,
            product: setting.product,
            productCurrentStock: setting.productCurrentStock,
            error: setting.error
        })
    )
    const { stockTransactionProduct, product, productCurrentStock, error } = useSelector(OutputsProperties)

    useEffect(() => {
        if (id) {
            // Charger les trois données en parallèle
            dispatch(getStockTransactionProduct(id))
            dispatch(getProduct(id))
            dispatch(getProductCurrentStock(id))
        }
    }, [dispatch, id])

    useEffect(() => {
        if (stockTransactionProduct && product && productCurrentStock) {
            console.log("Stock transactions:", stockTransactionProduct)
            console.log("Product info:", product)
            console.log("Current stock:", productCurrentStock)
            setIsLoading(false)
        }
    }, [stockTransactionProduct, product, productCurrentStock])

    // Calculer les statistiques à partir des transactions
    const statistics = useMemo(() => {
        const transactions = Array.isArray(stockTransactionProduct) 
            ? stockTransactionProduct 
            : (stockTransactionProduct?.data || stockTransactionProduct?.items || []);

        if (!transactions || transactions.length === 0) {
            return {
                totalPurchases: 0,
                totalSales: 0,
                totalPurchasesQty: 0,
                totalSalesQty: 0,
                purchaseValue: 0,
                salesValue: 0
            }
        }

        const stats = {
            totalPurchases: 0,
            totalSales: 0,
            totalPurchasesQty: 0,
            totalSalesQty: 0,
            purchaseValue: 0,
            salesValue: 0
        }

        transactions.forEach(transaction => {
            // Convertir explicitement en nombre pour éviter la concaténation
            const qty = Number(transaction.qty) || 0
            
            if (transaction.transactionType === 'PURCHASE') {
                stats.totalPurchasesQty += qty
                // Utiliser le prix d'achat du produit si disponible
                const rate = Number(product?.purchasePrice || transaction.rate || 0)
                stats.purchaseValue += qty * rate
                stats.totalPurchases++
            } else if (transaction.transactionType === 'SALE' || transaction.transactionType === 'Sales Invoice') {
                stats.totalSalesQty += qty
                // Utiliser le prix de vente du produit si disponible
                const rate = Number(product?.sellingPrice || transaction.rate || 0)
                stats.salesValue += qty * rate
                stats.totalSales++
            }
        })

        return stats
    }, [stockTransactionProduct, product])

    // Informations du produit avec les stocks actuels
    const productInfo = useMemo(() => {
        if (product) {
            return {
                name: product.name || 'N/A',
                productRef: product.productRef || 'N/A',
                qtyInWarehouse: productCurrentStock?.qtyInWarehouse || 0,
                qtyInPharmacy: productCurrentStock?.qtyInPharmacy || 0,
                totalStock: (productCurrentStock?.qtyInWarehouse || 0) + (productCurrentStock?.qtyInPharmacy || 0),
                purchasePrice: product.purchasePrice || 0,
                sellingPrice: product.sellingPrice || 0,
                category: product.category?.name || 'N/A',
                minQuantity: product.minQuantity || 0,
                description: product.description || ''
            }
        }
        return {
            name: 'N/A',
            productRef: 'N/A',
            qtyInWarehouse: 0,
            qtyInPharmacy: 0,
            totalStock: 0,
            purchasePrice: 0,
            sellingPrice: 0,
            category: 'N/A',
            minQuantity: 0,
            description: ''
        }
    }, [product, productCurrentStock])

   const formatCurrency = (value, showCurrency = true) => {
    if (showCurrency) {
        return new Intl.NumberFormat('fr-FR', {
            style: 'currency',
            currency: 'XAF',
            minimumFractionDigits: 0
        }).format(value)
    } else {
        return new Intl.NumberFormat('fr-FR', {
            minimumFractionDigits: 0
        }).format(value)
    }
}

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A'
        const date = new Date(dateString)
        return date.toLocaleDateString('fr-FR')
    }

    // Calculer le rate pour chaque transaction
    const calculateRate = (transaction) => {
        if (transaction.transactionType === 'PURCHASE') {
            return Number(product?.purchasePrice || transaction.rate || 0)
        } else if (transaction.transactionType === 'SALE' || transaction.transactionType === 'Sales Invoice') {
            return Number(product?.sellingPrice || transaction.rate || 0)
        }
        return Number(transaction.rate || 0)
    }

    return (
        <React.Fragment>
            {isLoading ? (
                <GlobalLoader />
            ) : (
                <div className="patients-list-content">
                    <div className="patients-list px-2 pb-5">
                        <div className="container-fluid">
                            {/* En-tête */}
                            <div className="heading-parent-sec">
                                <div className="heading-child-one-sec pt-4 d-flex justify-content-between align-items-center">
                                    <div>
                                        <button 
                                            className="btn btn-secondary me-3"
                                            onClick={() => props.router.navigate(-1)}
                                        >
                                            <i className="fa fa-arrow-left me-1"></i>
                                            {props.t("back") || "Retour"}
                                        </button>
                                        <span className="h6">{props.t("item_information") || "Information du produit"}</span>
                                    </div>
                                </div>
                            </div>

                            <Card className="border-0 mt-3">
                                <CardBody>
                                    {/* Informations du produit */}
                                    <Row className="mb-4">
                                        <Col md={12}>
                                            <div className="d-flex justify-content-between align-items-center mb-3">
                                                <div>
                                                    <h5 className="mb-1">{productInfo.name}</h5>
                                                    <small className="text-muted">
                                                        {props.t("reference") || "Référence"}: {productInfo.productRef} | 
                                                        {' '}{props.t("category") || "Catégorie"}: {productInfo.category}
                                                    </small>
                                                </div>
                                                <div className="d-flex gap-3 align-items-center">
                                                    <div className="text-center">
                                                        <small className="text-muted d-block">
                                                            <i className="fa fa-warehouse me-1"></i>
                                                            {props.t("Warehouse Quantity") || "Entrepôt"}
                                                        </small>
                                                        <div className=" fs-5 px-3 py-2">{productInfo.qtyInWarehouse}</div>
                                                    </div>
                                                    <div className="text-center">
                                                        <small className="text-muted d-block">
                                                            <i className="fa fa-store me-1"></i>
                                                            {props.t("Pharmacy Quantity") || "Pharmacie"}
                                                        </small>
                                                        <div className="fs-5 px-3 py-2">{productInfo.qtyInPharmacy}</div>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="d-flex gap-4">
                                                <div>
                                                    <small className="text-muted">{props.t("purchase_price") || "Prix d'achat"}:</small>
                                                    <div className="fw-bold text-success">{formatCurrency(productInfo.purchasePrice,false)}</div>
                                                </div>
                                                <div>
                                                    <small className="text-muted">{props.t("selling_price") || "Prix de vente"}:</small>
                                                    <div className="fw-bold text-primary">{formatCurrency(productInfo.sellingPrice,false)}</div>
                                                </div>
                                                <div>
                                                    <small className="text-muted">{props.t("min_quantity") || "Quantité min"}:</small>
                                                    <div className="fw-bold">{productInfo.minQuantity}</div>
                                                </div>
                                            </div>
                                        </Col>
                                    </Row>

                                    {/* Statistiques des transactions */}
                                    <div className="mb-4">
                                        <h6 className="mb-3">{props.t("transaction_summary") || "Résumé des transactions"}</h6>
                                        <Row>
                                            <Col md={6}>
                                                <Card className="border border-success">
                                                    <CardBody>
                                                        <div className="d-flex justify-content-between align-items-center">
                                                            <div>
                                                                <h6 className="text-success mb-2">
                                                                    <i className="fa fa-arrow-down me-2"></i>
                                                                    {props.t("total_purchases") || "Total Achats"}
                                                                </h6>
                                                                <div className="d-flex gap-3">
                                                                    <div>
                                                                        <small className="text-muted">{props.t("transactions") || "Transactions"}:</small>
                                                                        <div className="fw-bold">{statistics.totalPurchases}</div>
                                                                    </div>
                                                                    <div>
                                                                        <small className="text-muted">{props.t("quantity") || "Quantité"}:</small>
                                                                        <div className="fw-bold">{statistics.totalPurchasesQty}</div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <div className="text-end">
                                                                <small className="text-muted">{props.t("value") || "Valeur"}</small>
                                                                <h5 className="text-success mb-0">{formatCurrency(statistics.purchaseValue)}</h5>
                                                            </div>
                                                        </div>
                                                    </CardBody>
                                                </Card>
                                            </Col>
                                            <Col md={6}>
                                                <Card className="border border-danger">
                                                    <CardBody>
                                                        <div className="d-flex justify-content-between align-items-center">
                                                            <div>
                                                                <h6 className="text-danger mb-2">
                                                                    <i className="fa fa-arrow-up me-2"></i>
                                                                    {props.t("total_sales") || "Total Ventes"}
                                                                </h6>
                                                                <div className="d-flex gap-3">
                                                                    <div>
                                                                        <small className="text-muted">{props.t("transactions") || "Transactions"}:</small>
                                                                        <div className="fw-bold">{statistics.totalSales}</div>
                                                                    </div>
                                                                    <div>
                                                                        <small className="text-muted">{props.t("quantity") || "Quantité"}:</small>
                                                                        <div className="fw-bold">{statistics.totalSalesQty}</div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <div className="text-end">
                                                                <small className="text-muted">{props.t("value") || "Valeur"}</small>
                                                                <h5 className="text-danger mb-0">{formatCurrency(statistics.salesValue)}</h5>
                                                            </div>
                                                        </div>
                                                    </CardBody>
                                                </Card>
                                            </Col>
                                        </Row>
                                    </div>

                                    {/* Tableau des transactions récentes */}
                                    <div>
                                        <h6 className="mb-3">{props.t("recent_transactions") || "Transactions récentes"}</h6>
                                        <div className="table-responsive">
                                            <Table className="table table-bordered table-hover">
                                                <thead className="table-light">
                                                    <tr>
                                                        <th>{props.t("date") || "Date"}</th>
                                                        <th>{props.t("reference") || "Référence"}</th>
                                                        <th>{props.t("type") || "Type"}</th>
                                                        <th>{props.t("contact") || "Contact"}</th>
                                                        <th className="text-end">{props.t("quantity") || "Quantité"}</th>
                                                        <th className="text-end">{props.t("unit_price") || "Prix unitaire"}</th>
                                                        <th className="text-end">{props.t("total") || "Total"}</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {(() => {
                                                        const transactions = Array.isArray(stockTransactionProduct) 
                                                            ? stockTransactionProduct 
                                                            : (stockTransactionProduct?.data || stockTransactionProduct?.items || []);

                                                        return transactions && transactions.length > 0 ? (
                                                            transactions.map((transaction, index) => {
                                                                const rate = calculateRate(transaction)
                                                                const qty = Number(transaction.qty) || 0
                                                                const total = qty * rate
                                                                const isPurchase = transaction.transactionType === 'PURCHASE'
                                                                const isSale = transaction.transactionType === 'SALE' || transaction.transactionType === 'Sales Invoice'

                                                                return (
                                                                    <tr key={index}>
                                                                        <td>{formatDate(transaction.transactionDate)}</td>
                                                                        <td>{transaction.transactionRef || 'N/A'}</td>
                                                                        <td>
                                                                            <span className={`badge ${
                                                                                isPurchase ? 'bg-success' :
                                                                                isSale ? 'bg-danger' :
                                                                                'bg-info'
                                                                            }`}>
                                                                                {isPurchase ? (props.t("purchase") || "Achat") :
                                                                                 isSale ? (props.t("sale") || "Vente") :
                                                                                 transaction.transactionType}
                                                                            </span>
                                                                        </td>
                                                                        <td>{transaction.contact || 'N/A'}</td>
                                                                        <td className="text-end">
                                                                            <span className={isPurchase ? 'text-success fw-bold' : isSale ? 'text-danger fw-bold' : ''}>
                                                                                {isPurchase ? '+' : isSale ? '-' : ''}{qty}
                                                                            </span>
                                                                        </td>
                                                                        <td className="text-end">{formatCurrency(rate,false)}</td>
                                                                        <td className="text-end fw-bold">
                                                                            <span className={isPurchase ? 'text-success' : isSale ? 'text-danger' : ''}>
                                                                                {formatCurrency(total,false)}
                                                                            </span>
                                                                        </td>
                                                                    </tr>
                                                                )
                                                            })
                                                        ) : (
                                                            <tr>
                                                                <td colSpan="7" className="text-center py-4">
                                                                    <i className="fa fa-inbox fa-2x text-muted mb-2"></i>
                                                                    <div>{props.t("no_transactions") || "Aucune transaction trouvée"}</div>
                                                                </td>
                                                            </tr>
                                                        )
                                                    })()}
                                                </tbody>
                                            </Table>
                                        </div>
                                    </div>
                                </CardBody>
                            </Card>
                        </div>
                    </div>
                </div>
            )}
        </React.Fragment>
    )
}

export default withRouter(withTranslation()(StockTransactionDetails))